<app-page-section>
  <div class="mb-4">
    <p class="text-uppercase text-primary fw-semibold small mb-2">Admin</p>
    <h1 class="display-5 fw-bold">Operations panel</h1>
    <p class="lead text-muted">Run protected platform operations and inspect resource status.</p>
  </div>
  @if (error) { <div class="alert alert-danger">{{ error }}</div> }

  <div class="admin-card mb-4">
    <div class="d-flex flex-wrap align-items-center justify-content-between gap-3 mb-3">
      <div>
        <h2 class="h5 mb-1">User roles</h2>
        <p class="text-muted small mb-0">Promote or demote users between ADMIN and MODO Cognito groups. Admins are moderators too.</p>
      </div>
      <button class="btn btn-outline-primary btn-sm" type="button" (click)="loadUsers()" [disabled]="isLoading || isLoadingUsers">Refresh users</button>
    </div>
    <div class="row g-3 align-items-end mb-3">
      <div class="col-lg-6">
        <label class="form-label" for="adminUserSearch">Search users</label>
        <input id="adminUserSearch" class="form-control" type="search" [(ngModel)]="userSearchTerm" placeholder="Name, surname, email, username, status...">
      </div>
      <div class="col-sm-6 col-lg-3">
        <label class="form-label" for="adminUserStatusFilter">Status</label>
        <select id="adminUserStatusFilter" class="form-select" [(ngModel)]="userStatusFilter">
          <option value="all">All statuses</option>
          @for (status of availableUserStatuses; track status) { <option [value]="status">{{ status }}</option> }
        </select>
      </div>
      <div class="col-sm-6 col-lg-3">
        <label class="form-label" for="adminUserGroupFilter">Group</label>
        <select id="adminUserGroupFilter" class="form-select" [(ngModel)]="userGroupFilter">
          <option value="all">All groups</option>
          <option value="admin">Admin</option>
          <option value="modo">Modo</option>
          <option value="not_promoted">Not promoted</option>
        </select>
      </div>
    </div>
    <div class="table-responsive admin-users-table-shell">
      <table class="table align-middle mb-0">
        <thead><tr><th>User</th><th>Status</th><th>Groups</th><th class="text-end">Roles</th></tr></thead>
        <tbody>
          @if (isLoadingUsers) {
            <tr><td colspan="4"><div class="admin-loading-panel"><app-loading-spinner label="Loading users"></app-loading-spinner></div></td></tr>
          } @else {
          @for (user of filteredAdminUsers; track user.username) {
            <tr>
              <td><strong>{{ userDisplayName(user) }}</strong><br><span class="small text-muted">{{ user.email || user.username }}</span>@if (user.email) { <br><span class="small text-muted">{{ user.username }}</span> }</td>
              <td><span class="badge" [class.text-bg-success]="user.enabled" [class.text-bg-secondary]="!user.enabled">{{ userStatusLabel(user) }}</span></td>
              <td>
                @if (user.groups.length) {
                  @for (group of user.groups; track group) { <span class="badge text-bg-light me-1">{{ group }}</span> }
                } @else {
                  <span class="text-muted small">No privileged groups</span>
                }
              </td>
              <td class="text-end">
                <div class="role-switches">
                  <div class="form-check form-switch">
                    <input class="form-check-input" type="checkbox" role="switch" [id]="'adminSwitch-' + user.username" [ngModel]="user.isAdmin" (ngModelChange)="setUserRole(user, 'ADMIN', $event)" [disabled]="isLoading || isLoadingUsers">
                    <label class="form-check-label" [for]="'adminSwitch-' + user.username">ADMIN</label>
                  </div>
                  <div class="form-check form-switch">
                    <input class="form-check-input" type="checkbox" role="switch" [id]="'modoSwitch-' + user.username" [ngModel]="user.groups.includes('MODO')" (ngModelChange)="setUserRole(user, 'MODO', $event)" [disabled]="isLoading || isLoadingUsers || (user.isAdmin && !user.groups.includes('MODO'))">
                    <label class="form-check-label" [for]="'modoSwitch-' + user.username">MODO</label>
                  </div>
                </div>
              </td>
            </tr>
          } @empty {
            <tr><td colspan="4" class="text-muted">No users match the current filters.</td></tr>
          }
          }
        </tbody>
      </table>
    </div>
    @if (nextUsersToken) { <button class="btn btn-outline-primary btn-sm mt-3" type="button" (click)="loadUsers(nextUsersToken)" [disabled]="isLoading || isLoadingUsers">Load more users</button> }
  </div>

  <div class="soft-card bg-white visits-section mb-4 p-4">
    <div class="visits-trend line-chart">
      <div class="d-flex flex-wrap align-items-start justify-content-between gap-3 mb-4">
        <div>
          <p class="text-uppercase text-primary fw-semibold small mb-1">Traffic</p>
          <h2 class="h5 mb-1">Total visits</h2>
          <p class="text-muted small mb-0">Last {{ visitTrend.length || 14 }} days</p>
        </div>
        @if (isLoadingStatus) { <div class="visit-total-stat visit-total-stat-loading"><span>Total</span><strong>...</strong></div> }
        @else if (status?.metrics?.totalVisits?.error) { <span class="badge text-bg-danger">error</span> }
        @else if (status?.metrics?.totalVisits?.configured === false) { <span class="badge text-bg-secondary">not configured</span> }
        @else { <div class="visit-total-stat"><span>Total</span><strong>{{ status?.metrics?.totalVisits?.total || 0 }}</strong></div> }
      </div>
      @if (isLoadingStatus) {
        <div class="visit-chart visit-chart-loading line-chart-plot"><app-loading-spinner label="Loading traffic"></app-loading-spinner></div>
        <div class="line-chart-labels visit-chart-labels mt-2"><span>&nbsp;</span><span>&nbsp;</span><span>&nbsp;</span></div>
      } @else if (visitTrend.length) {
        <div class="visit-chart line-chart-plot" role="img" [attr.aria-label]="'Visits trend over the last ' + visitTrend.length + ' days, total ' + visitTrendTotal" (pointermove)="updateVisitCursorGuide($event)" (pointerleave)="hideVisitCursorGuide()">
          @if (activeVisitPoint) {
            <div class="chart-tooltip-panel" [style.left.%]="visitTooltipLeftPercent" aria-live="polite">
              <strong>{{ visitDateLabel(activeVisitPoint.point.date) }}</strong>
              <span>{{ activeVisitPoint.point.count }} visits</span>
            </div>
          }
          @if (visitCursorGuideX !== null) {
            <div class="chart-cursor-guide" [style.left.%]="visitCursorGuideLeftPercent"></div>
          }
          <svg class="visit-line" viewBox="0 0 100 100" preserveAspectRatio="none" aria-hidden="true" focusable="false">
            <g class="grid-lines">
              <line x1="0" y1="7" x2="100" y2="7"></line>
              <line x1="0" y1="28.5" x2="100" y2="28.5"></line>
              <line x1="0" y1="50" x2="100" y2="50"></line>
              <line x1="0" y1="71.5" x2="100" y2="71.5"></line>
              <line x1="0" y1="93" x2="100" y2="93"></line>
            </g>
            <path class="visit-line-path" [attr.d]="visitTrendPath"></path>
          </svg>
          @if (activeVisitPoint) {
            <span class="visit-line-point active" [style.left.%]="visitPointX(activeVisitPoint.index)" [style.top.%]="visitPointY(activeVisitPoint.point)" aria-hidden="true"></span>
          }
          <div class="visit-bars" [style.--visit-columns]="visitTrend.length" aria-hidden="true">
            @for (point of visitTrend; track point.date) {
              <div class="visit-bar-column" [style.--visit-bar-height]="visitBarHeight(point) + '%'" [title]="visitDateLabel(point.date) + ': ' + point.count + ' visits'">
                @if (point.count > 0) { <span class="visit-bar-value">{{ point.count }}</span> }
                <span class="visit-bar" [style.height.%]="visitBarHeight(point)"></span>
              </div>
            }
          </div>
        </div>
        <div class="line-chart-labels visit-chart-labels mt-2">
          <span>{{ visitDateLabel(visitTrend[0].date) }}</span>
          <span>{{ visitTrendTotal }} visits in period</span>
          <span>{{ visitDateLabel(visitTrend[visitTrend.length - 1].date) }}</span>
        </div>
      } @else {
        <p class="text-muted small mb-0">Daily visit history will appear after the next tracked visits.</p>
      }
    </div>
  </div>

  <div class="row g-4">
    <div class="col-lg-4">
      <div class="admin-card h-100">
        <h2 class="h5">Resource status</h2>
        <p class="text-muted small">Environment: {{ status?.environment || 'unknown' }}</p>
        <button class="btn btn-outline-primary btn-sm mb-3" type="button" (click)="refreshStatus()" [disabled]="isLoading || isLoadingStatus">Refresh</button>
        @if (isLoadingStatus) {
          <div class="admin-loading-panel resource-loading-panel"><app-loading-spinner label="Loading resources"></app-loading-spinner></div>
        } @else {
        @for (queue of status?.queues || []; track queue.label) {
          <div class="queue-row">
            <strong>{{ queue.label }}</strong>
            @if (!queue.configured) { <span class="badge text-bg-secondary">not configured</span> }
            @else if (queue.error) { <span class="badge text-bg-danger">error</span> }
            @else { <span class="badge text-bg-primary">{{ queue.visible || 0 }} visible</span> <span class="badge text-bg-light">{{ queue.inFlight || 0 }} in flight</span> }
          </div>
        }
        }
      </div>
    </div>

    <div class="col-lg-8">
      <div class="admin-card h-100">
        <div class="d-flex flex-wrap align-items-start justify-content-between gap-3 mb-3">
          <div>
            <h2 class="h5 mb-1">Offer processing pipeline</h2>
            <p class="text-muted small mb-0">Run each operational step from ingestion to notifications.</p>
          </div>
          @if (isLoadingStatus) { <span class="badge text-bg-light"><span class="spinner-border spinner-border-sm me-1" aria-hidden="true"></span>Loading config</span> }
          @else { <span class="badge text-bg-light">Timeline view</span> }
        </div>

        <div class="pipeline-timeline">
          <section class="pipeline-step">
            <div class="pipeline-marker">1</div>
            <div class="pipeline-content">
              <p class="text-uppercase text-primary fw-semibold small mb-1">Ingest offers</p>
              <h3 class="h6 mb-2">Force crawl</h3>
              <p class="text-muted small">Fetch offers from one source or all configured crawlers.</p>
              <div class="pipeline-controls">
                <select class="form-select" [(ngModel)]="selectedSource" aria-label="Crawler source">
                  @for (source of crawlSources; track source) { <option [value]="source">{{ source }}</option> }
                </select>
                <button class="btn btn-danger" type="button" (click)="forceCrawl()" [disabled]="isLoading">Force crawl</button>
              </div>
            </div>
          </section>

          <section class="pipeline-step">
            <div class="pipeline-marker">2</div>
            <div class="pipeline-content">
              <p class="text-uppercase text-primary fw-semibold small mb-1">Extract fields</p>
              <h3 class="h6 mb-2">AI extraction test</h3>
              <p class="text-muted small">Paste one offer for a smoke test, or queue extraction for active existing offers.</p>
              <button class="btn btn-primary mb-3" type="button" (click)="extractExistingOffers()" [disabled]="isLoading">Extract existing offers</button>
              <div class="row g-3">
                <div class="col-md-6">
                  <label class="form-label" for="extractionTitle">Offer title</label>
                  <input id="extractionTitle" class="form-control" type="text" [(ngModel)]="extractionDraft.title" placeholder="PhD position in computational biology">
                </div>
                <div class="col-md-6">
                  <label class="form-label" for="extractionOrganization">Organization</label>
                  <input id="extractionOrganization" class="form-control" type="text" [(ngModel)]="extractionDraft.organization" placeholder="University / lab">
                </div>
                <div class="col-md-6">
                  <label class="form-label" for="extractionLocation">Location</label>
                  <input id="extractionLocation" class="form-control" type="text" [(ngModel)]="extractionDraft.location" placeholder="City, country">
                </div>
                <div class="col-md-6">
                  <label class="form-label" for="extractionSourceUrl">Source URL</label>
                  <input id="extractionSourceUrl" class="form-control" type="url" [(ngModel)]="extractionDraft.sourceUrl" placeholder="https://...">
                </div>
                <div class="col-12">
                  <label class="form-label" for="extractionDescription">Raw offer text</label>
                  <textarea id="extractionDescription" class="form-control" rows="5" [(ngModel)]="extractionDraft.description" placeholder="Paste the offer description, requirements, funding, deadline, and application instructions here."></textarea>
                </div>
              </div>
              <button class="btn btn-outline-primary mt-3" type="button" (click)="testExtraction()" [disabled]="isLoading">Run AI extraction test</button>
            </div>
          </section>

          <section class="pipeline-step">
            <div class="pipeline-marker">3</div>
            <div class="pipeline-content">
              <p class="text-uppercase text-primary fw-semibold small mb-1">Match and notify</p>
              <h3 class="h6 mb-2">Matching & batches</h3>
              <p class="text-muted small">Choose the next matching pipeline operation to queue.</p>
              <div class="pipeline-controls">
                <select class="form-select" [(ngModel)]="selectedMatchingAction" aria-label="Matching pipeline action">
                  @for (action of matchingActions; track action) { <option [value]="action">{{ matchingActionLabel(action) }}</option> }
                </select>
                <button class="btn btn-warning" type="button" (click)="runMatching()" [disabled]="isLoading">Run action</button>
              </div>
            </div>
          </section>
        </div>
      </div>
    </div>

    <div class="col-lg-6">
      <div class="admin-card h-100">
        <h2 class="h5">API tester</h2>
        <p class="text-muted small">Runs a protected admin smoke endpoint and returns latency.</p>
        <button class="btn btn-outline-primary" type="button" (click)="testApi()" [disabled]="isLoading">Test admin API</button>
      </div>
    </div>

    <div class="col-lg-6">
      <div class="admin-card h-100">
        <h2 class="h5">Test notification</h2>
        <select class="form-select mb-3" [(ngModel)]="testNotificationChannel" aria-label="Notification channel">
          <option value="email">Email</option>
          <option value="whatsapp">WhatsApp</option>
        </select>
        @if (testNotificationChannel === 'email') {
          <input class="form-control mb-3" type="email" [(ngModel)]="testRecipient" [placeholder]="session.currentUser()?.email || 'recipient@example.com'">
        } @else {
          <input class="form-control mb-3" type="tel" [(ngModel)]="testRecipient" placeholder="+33651310649">
          <input class="form-control mb-3" type="text" [(ngModel)]="testNotificationName" placeholder="Jean-Baptiste">
        }
        <button class="btn btn-outline-primary" type="button" (click)="sendTestNotification()" [disabled]="isLoading">Send test notification</button>
      </div>
    </div>

  </div>

  @if (lastResult) {
    <div class="admin-card mt-4">
      <h2 class="h5">Last result</h2>
      <pre class="mb-0">{{ lastResult | json }}</pre>
    </div>
  }
</app-page-section>